import {useEffect, useMemo, useState} from 'react'
import {useLazyQuery, useMutation, useQuery} from '@apollo/client'
import {Breadcrumb, Button, ButtonGroup, Card, Col, Dropdown, Form, InputGroup, Modal, Row, Spinner, Table} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {hasPermission, parseId} from '../../helpers'
import {ALL_PERMISSIONS, SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {PaginationSection} from '../shared/PaginationSection'
import {PaginationUtils} from '../shared/PaginationUtils'
import {GET_ASSIGNMENT_USERS_LIST} from '../../gql/queries/usersQuery'
import {GET_TICKET_ASSIGNMENT_GROUPS} from '../../gql/queries/assignmentGroupQuery'
import {ARCHIVE_TICKET, DELETE_TICKET} from '../../gql/mutations/ticketsMutations'
import {GET_TICKET_BY_ID, GET_TICKET_DASHBOARD, GET_TICKET_METRICS, GET_TICKETS, GET_TICKET_TYPES} from '../../gql/queries/ticketsQuery'
import {GET_MY_POLICY_FILTERS} from '../../gql/queries/policyFiltersQueries'
import {CREATE_POLICY_FILTER, DELETE_POLICY_FILTER, UPDATE_POLICY_FILTER} from '../../gql/mutations/policyFilterMutations'
import {useGetUser} from '../../store/userStore'
import {IResTicketById, IResTicketDashboard, IResTicketMetrics, IResTicketMutation, IResTicketTypes, IResTickets, ITicket, ITicketFilters, ITicketSavedFilter} from '../../types/tickets'
import {AppliedFiltersList} from '../policies/components/AppliedFiltersList'
import {ModalTicketFilters} from './components/ModalTicketFilters'
import './AttentionTicketsPage.css'

const statusVariant = (status: string) => {
  if (status === 'OPEN') return 'warning'
  if (status === 'IN_PROGRESS') return 'primary'
  if (status === 'WAITING_CUSTOMER') return 'info'
  if (status === 'RESOLVED') return 'success'
  return 'secondary'
}

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString()
}

const userLabel = (user?: {firstName?: string; lastName?: string; email?: string} | null) => {
  if (!user) return '-'
  const full = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  return full || user.email || '-'
}

const channelLabel = (channel?: string) => {
  if (!channel) return '-'
  if (channel === 'INTERNAL') return 'Internal'
  if (channel === 'PHONE') return 'Phone'
  if (channel === 'WHATSAPP') return 'WhatsApp'
  if (channel === 'SMS') return 'SMS'
  if (channel === 'EMAIL') return 'Email'
  if (channel === 'WEB') return 'Web'
  if (channel === 'API') return 'API'
  return channel
}

const assignmentLabel = (ticket?: Pick<ITicket, 'assignedGroup' | 'assignedUser'> | null) => {
  if (!ticket) return '-'
  const groupName = ticket.assignedGroup?.name || ''
  const userName = userLabel(ticket.assignedUser)
  if (groupName && userName !== '-') return `${groupName} / ${userName}`
  if (groupName) return groupName
  return userName
}

const enumLabel = (value?: string | null) => {
  if (!value) return '-'
  return value.replace(/_/g, ' ')
}

const priorityVariant = (priority?: string | null) => {
  if (priority === 'URGENT') return 'danger'
  if (priority === 'HIGH') return 'warning'
  if (priority === 'MEDIUM') return 'info'
  return 'secondary'
}

const tableStatusBadgeClass = (status?: string | null) => {
  if (status === 'OPEN') return 'attention-tickets-page__pill--status-open'
  if (status === 'IN_PROGRESS') return 'attention-tickets-page__pill--status-in-progress'
  if (status === 'WAITING_CUSTOMER') return 'attention-tickets-page__pill--status-waiting-customer'
  if (status === 'RESOLVED') return 'attention-tickets-page__pill--status-resolved'
  if (status === 'CLOSED') return 'attention-tickets-page__pill--status-closed'
  return 'attention-tickets-page__pill--status-default'
}

const tablePriorityBadgeClass = (priority?: string | null) => {
  if (priority === 'URGENT') return 'attention-tickets-page__pill--priority-urgent'
  if (priority === 'HIGH') return 'attention-tickets-page__pill--priority-high'
  if (priority === 'MEDIUM') return 'attention-tickets-page__pill--priority-medium'
  if (priority === 'LOW') return 'attention-tickets-page__pill--priority-low'
  return 'attention-tickets-page__pill--priority-default'
}

const escapeHtml = (value?: string | null) =>
  (value || '-')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const toPrintText = (value?: string | null) => escapeHtml(value).replace(/\n/g, '<br/>')

const TICKETS_FILTERS_STORAGE_KEY_PREFIX = 'tickets_filters_v1'

const toFilterArray = (value: string | string[] | null | undefined) => {
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value === 'string' && value) return [value]
  return []
}

const INITIAL_TICKET_FILTERS: ITicketFilters = {
  search: '',
  status: '',
  channel: '',
  priority: '',
  ticketTypeId: '',
  assignmentType: '',
  assignedUserId: '',
  assignedGroupId: '',
  createdById: '',
  createdAfter: '',
  createdBefore: '',
  dueAfter: '',
  dueBefore: '',
  pendingInbox: false,
  onlyOverdue: false,
  onlyUnassigned: false,
  inboxMode: '',
}

type TicketSortColumn = 'id' | 'subject' | 'createdAt'
type TicketSortDirection = 'asc' | 'desc'

export const AttentionTicketsPage = () => {
  const navigate = useNavigate()
  const currentUserId = useGetUser((s) => s.user?.user?.id) || 'anonymous'
  const permissions = useGetUser((s) => s.user?.permissions) ?? []
  const canArchiveTicket = hasPermission(permissions, ALL_PERMISSIONS.ARCHIVE_TICKET)
  const canDeleteTicket = hasPermission(permissions, ALL_PERMISSIONS.DELETE_TICKET)
  const ticketsFiltersStorageKey = `${TICKETS_FILTERS_STORAGE_KEY_PREFIX}_${currentUserId}`

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<string | string[]>('')
  const [channel, setChannel] = useState('')
  const [priority, setPriority] = useState<string | string[]>('')
  const [ticketTypeId, setTicketTypeId] = useState<string | string[]>('')
  const [assignmentType, setAssignmentType] = useState('')
  const [assignedUserId, setAssignedUserId] = useState<string | string[]>('')
  const [assignedGroupId, setAssignedGroupId] = useState<string | string[]>('')
  const [createdById, setCreatedById] = useState<string | string[]>('')
  const [createdAfter, setCreatedAfter] = useState('')
  const [createdBefore, setCreatedBefore] = useState('')
  const [dueAfter, setDueAfter] = useState('')
  const [dueBefore, setDueBefore] = useState('')
  const [pendingInbox, setPendingInbox] = useState(false)
  const [onlyOverdue, setOnlyOverdue] = useState(false)
  const [onlyUnassigned, setOnlyUnassigned] = useState(false)
  const [inboxMode, setInboxMode] = useState('')
  const [showSolvedClosedInList, setShowSolvedClosedInList] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [quickSearch, setQuickSearch] = useState('')
  const [isOpenFiltersModal, setIsOpenFiltersModal] = useState(false)
  const [draftFilters, setDraftFilters] = useState<ITicketFilters>(INITIAL_TICKET_FILTERS)
  const [previewTicket, setPreviewTicket] = useState<ITicket | null>(null)
  const [printingTicketId, setPrintingTicketId] = useState<string | null>(null)
  const [sortColumn, setSortColumn] = useState<TicketSortColumn>('createdAt')
  const [sortDirection, setSortDirection] = useState<TicketSortDirection>('desc')

  const [fetchTickets, {data, loading, error, refetch}] = useLazyQuery<IResTickets>(GET_TICKETS, {
    fetchPolicy: 'no-cache',
  })
  const {data: dataTicketTypes} = useQuery<IResTicketTypes>(GET_TICKET_TYPES, {
    variables: {isActive: true},
    fetchPolicy: 'cache-first',
  })
  const {data: dataUsers} = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    variables: {assignmentCandidates: true},
    fetchPolicy: 'cache-first',
  })
  const {data: dataGroups} = useQuery(GET_TICKET_ASSIGNMENT_GROUPS, {fetchPolicy: 'cache-first'})
  const {data: dataSavedFilters, refetch: refetchSavedFilters} = useQuery<{
    myPolicyFilters: {
      id: string
      name: string
      scope?: string
      filters: string | ITicketFilters
      created?: string
      updated?: string
    }[]
  }>(GET_MY_POLICY_FILTERS, {
    fetchPolicy: 'network-only',
    variables: {scope: 'TICKET'},
  })
  const [fetchTicketById] = useLazyQuery<IResTicketById>(GET_TICKET_BY_ID, {fetchPolicy: 'no-cache'})
  const [deleteTicket] = useMutation<{deleteTicket: IResTicketMutation}>(DELETE_TICKET)
  const [archiveTicket] = useMutation(ARCHIVE_TICKET)
  const [createSavedFilter] = useMutation(CREATE_POLICY_FILTER)
  const [updateSavedFilter] = useMutation(UPDATE_POLICY_FILTER)
  const [deleteSavedFilter] = useMutation(DELETE_POLICY_FILTER)
  const [
    fetchPreviewTicketById,
    {data: previewTicketData, loading: previewTicketLoading, error: previewTicketError},
  ] = useLazyQuery<IResTicketById>(GET_TICKET_BY_ID, {fetchPolicy: 'no-cache'})

  const parseTicketFilterPayload = (rawFilters: string | ITicketFilters | undefined) => {
    if (!rawFilters) return INITIAL_TICKET_FILTERS
    if (typeof rawFilters === 'string') {
      try {
        const parsed = JSON.parse(rawFilters) as ITicketFilters
        return {...INITIAL_TICKET_FILTERS, ...parsed}
      } catch {
        return INITIAL_TICKET_FILTERS
      }
    }
    return {...INITIAL_TICKET_FILTERS, ...rawFilters}
  }

  const currentFilters: ITicketFilters = useMemo(
    () => ({
      search,
      status,
      channel,
      priority,
      ticketTypeId,
      assignmentType,
      assignedUserId,
      assignedGroupId,
      createdById,
      createdAfter,
      createdBefore,
      dueAfter,
      dueBefore,
      pendingInbox,
      onlyOverdue,
      onlyUnassigned,
      inboxMode,
    }),
    [
      assignedGroupId,
      assignedUserId,
      assignmentType,
      channel,
      createdAfter,
      createdBefore,
      createdById,
      dueAfter,
      dueBefore,
      inboxMode,
      onlyOverdue,
      onlyUnassigned,
      pendingInbox,
      priority,
      search,
      status,
      ticketTypeId,
    ]
  )

  const appliedFilters = useMemo(
    () => ({
      ...currentFilters,
      pendingInbox: currentFilters.pendingInbox ? true : undefined,
      onlyOverdue: currentFilters.onlyOverdue ? true : undefined,
      onlyUnassigned: currentFilters.onlyUnassigned ? true : undefined,
    }),
    [currentFilters]
  )

  const setFiltersFromObject = (nextFilters: ITicketFilters) => {
    setSearch(nextFilters.search || '')
    setStatus(nextFilters.status || '')
    setChannel(nextFilters.channel || '')
    setPriority(nextFilters.priority || '')
    setTicketTypeId(nextFilters.ticketTypeId || '')
    setAssignmentType(nextFilters.assignmentType || '')
    setAssignedUserId(nextFilters.assignedUserId || '')
    setAssignedGroupId(nextFilters.assignedGroupId || '')
    setCreatedById(nextFilters.createdById || '')
    setCreatedAfter(nextFilters.createdAfter || '')
    setCreatedBefore(nextFilters.createdBefore || '')
    setDueAfter(nextFilters.dueAfter || '')
    setDueBefore(nextFilters.dueBefore || '')
    setPendingInbox(Boolean(nextFilters.pendingInbox))
    setOnlyOverdue(Boolean(nextFilters.onlyOverdue))
    setOnlyUnassigned(Boolean(nextFilters.onlyUnassigned))
    setInboxMode(nextFilters.inboxMode || '')
  }

  useEffect(() => {
    try {
      const raw = localStorage.getItem(ticketsFiltersStorageKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as ITicketFilters
      setFiltersFromObject(parseTicketFilterPayload(parsed))
    } catch (error) {
      console.warn('Could not restore ticket filters from localStorage', error)
    }
  }, [ticketsFiltersStorageKey])

  useEffect(() => {
    localStorage.setItem(ticketsFiltersStorageKey, JSON.stringify(currentFilters))
  }, [currentFilters, ticketsFiltersStorageKey])

  useEffect(() => {
    setQuickSearch(search)
  }, [search])

  const sharedTicketQueryVariables = useMemo(
    () => ({
      assignedUserId:
        !Array.isArray(assignedUserId) && assignedUserId ? parseId(assignedUserId) : null,
      assignedUserIds: toFilterArray(assignedUserId).length
        ? toFilterArray(assignedUserId).map((value) => parseId(value))
        : null,
      createdById: !Array.isArray(createdById) && createdById ? parseId(createdById) : null,
      createdByIds: toFilterArray(createdById).length
        ? toFilterArray(createdById).map((value) => parseId(value))
        : null,
      search: search.trim() || null,
      status: !Array.isArray(status) && status ? status : null,
      statuses: toFilterArray(status).length ? toFilterArray(status) : null,
      channel: channel || null,
      priority: !Array.isArray(priority) && priority ? priority : null,
      priorities: toFilterArray(priority).length ? toFilterArray(priority) : null,
      ticketTypeId: !Array.isArray(ticketTypeId) && ticketTypeId ? parseId(ticketTypeId) : null,
      ticketTypeIds: toFilterArray(ticketTypeId).length
        ? toFilterArray(ticketTypeId).map((value) => parseId(value))
        : null,
      assignmentType: assignmentType || null,
      assignedGroupId:
        !Array.isArray(assignedGroupId) && assignedGroupId ? parseId(assignedGroupId) : null,
      assignedGroupIds: toFilterArray(assignedGroupId).length
        ? toFilterArray(assignedGroupId).map((value) => parseId(value))
        : null,
      createdAfter: createdAfter || null,
      createdBefore: createdBefore || null,
      dueAfter: dueAfter ? new Date(dueAfter).toISOString() : null,
      dueBefore: dueBefore ? new Date(dueBefore).toISOString() : null,
      includeArchived: false,
      onlyOverdue,
      onlyUnassigned,
      inboxMode: inboxMode || null,
      pendingInbox,
    }),
    [
      search,
      status,
      channel,
      priority,
      ticketTypeId,
      assignmentType,
      assignedUserId,
      assignedGroupId,
      createdById,
      createdAfter,
      createdBefore,
      dueAfter,
      dueBefore,
      onlyOverdue,
      onlyUnassigned,
      inboxMode,
      pendingInbox,
    ]
  )

  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery<IResTicketDashboard>(
    GET_TICKET_DASHBOARD,
    {
      variables: {...sharedTicketQueryVariables, days: 30},
      fetchPolicy: 'no-cache',
    }
  )
  const {
    data: dataTicketMetrics,
    loading: loadingTicketMetrics,
    error: ticketMetricsError,
  } = useQuery<IResTicketMetrics>(
    GET_TICKET_METRICS,
    {
      variables: sharedTicketQueryVariables,
      fetchPolicy: 'no-cache',
    }
  )
  const {data: dataUrgentTickets, loading: loadingUrgentTickets} = useQuery<IResTickets>(GET_TICKETS, {
    variables: {
      ...sharedTicketQueryVariables,
      first: 1,
      offset: 0,
      priority: 'URGENT',
    },
    fetchPolicy: 'no-cache',
  })
  const dashboard = dashboardData?.ticketDashboard
  const statusCounts = dashboard?.statusCounts || []
  const activeTicketsCount = useMemo(
    () =>
      statusCounts
        .filter((item) => ['OPEN', 'IN_PROGRESS', 'WAITING_CUSTOMER'].includes(item.status))
        .reduce((sum, item) => sum + item.count, 0),
    [statusCounts]
  )
  const resolvedCount = useMemo(
    () =>
      statusCounts
        .filter((item) => ['RESOLVED', 'CLOSED'].includes(item.status))
        .reduce((sum, item) => sum + item.count, 0),
    [statusCounts]
  )
  const urgentTicketsCount = dataUrgentTickets?.tickets?.totalCount || 0
  const overdueTicketsCount = dataTicketMetrics?.ticketMetrics?.overdueCount || 0

  useEffect(() => {
    fetchTickets({
      variables: {
        ...sharedTicketQueryVariables,
        includeSolvedClosed: showSolvedClosedInList || toFilterArray(status).length > 0,
        first,
        offset: (currentPage - 1) * first,
      },
    })
  }, [
    currentPage,
    fetchTickets,
    first,
    showSolvedClosedInList,
    status,
    sharedTicketQueryVariables,
  ])

  useEffect(() => {
    if (error) toast.error(`Error loading tickets: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (dashboardError) toast.error(`Error loading ticket dashboard: ${dashboardError.message}`)
  }, [dashboardError])

  useEffect(() => {
    if (ticketMetricsError) toast.error(`Error loading ticket metrics: ${ticketMetricsError.message}`)
  }, [ticketMetricsError])

  useEffect(() => {
    if (!previewTicket) return
    fetchPreviewTicketById({variables: {id: parseId(previewTicket.id)}})
  }, [fetchPreviewTicketById, previewTicket])

  const previewTicketInfo = useMemo(() => {
    if (!previewTicket) return null
    const fullTicket = previewTicketData?.ticketById
    if (fullTicket?.id === previewTicket.id) return fullTicket
    return previewTicket
  }, [previewTicket, previewTicketData])

  const tickets = data?.tickets?.items || []
  const totalTickets = data?.tickets?.totalCount || 0
  const totalPages = Math.ceil(totalTickets / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalTickets)
  const sortedTickets = useMemo(() => {
    const list = [...tickets]
    list.sort((a, b) => {
      if (sortColumn === 'id') {
        const aId = Number(parseId(a.id))
        const bId = Number(parseId(b.id))
        return sortDirection === 'asc' ? aId - bId : bId - aId
      }
      if (sortColumn === 'subject') {
        const aSubject = (a.title || '').toLowerCase()
        const bSubject = (b.title || '').toLowerCase()
        const result = aSubject.localeCompare(bSubject)
        return sortDirection === 'asc' ? result : -result
      }
      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate
    })
    return list
  }, [tickets, sortColumn, sortDirection])

  const users = useMemo(() => dataUsers?.allUsersList || [], [dataUsers])
  const groups = useMemo(() => dataGroups?.ticketAssignmentGroups || [], [dataGroups])
  const ticketTypes = useMemo(() => dataTicketTypes?.ticketTypes || [], [dataTicketTypes])

  const savedTicketFilters: ITicketSavedFilter[] =
    dataSavedFilters?.myPolicyFilters?.map((filter) => ({
      id: filter.id,
      name: filter.name,
      created: filter.created,
      updated: filter.updated,
      filters: parseTicketFilterPayload(filter.filters),
    })) || []

  const handleFilterErrors = (
    errors: {field?: string; message?: string}[] | undefined,
    fallback: string
  ) => {
    toast.error(errors?.[0]?.message || fallback)
  }

  const handleSaveTicketFilter = async (name: string, filters: ITicketFilters) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Filter name is required.')
      return null
    }

    const result = await createSavedFilter({
      variables: {
        policyFilterData: {
          name: trimmedName,
          scope: 'TICKET',
          filters: JSON.stringify(filters),
        },
      },
    })
    const payload = result.data?.createPolicyFilter
    if (!payload?.success) {
      handleFilterErrors(payload?.errors, 'Unable to save filter.')
      return null
    }

    await refetchSavedFilters()
    toast.success('Filter saved.')
    if (!payload.policyFilter) return null
    return {
      id: payload.policyFilter.id,
      name: payload.policyFilter.name,
      filters: parseTicketFilterPayload(payload.policyFilter.filters),
      updated: payload.policyFilter.updated,
    } as ITicketSavedFilter
  }

  const handleUpdateTicketFilter = async (id: string, name: string, filters: ITicketFilters) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Filter name is required.')
      return null
    }

    const result = await updateSavedFilter({
      variables: {
        id: parseId(id),
        policyFilterData: {
          name: trimmedName,
          scope: 'TICKET',
          filters: JSON.stringify(filters),
        },
      },
    })
    const payload = result.data?.updatePolicyFilter
    if (!payload?.success) {
      handleFilterErrors(payload?.errors, 'Unable to update filter.')
      return null
    }

    await refetchSavedFilters()
    toast.success('Filter updated.')
    if (!payload.policyFilter) return null
    return {
      id: payload.policyFilter.id,
      name: payload.policyFilter.name,
      filters: parseTicketFilterPayload(payload.policyFilter.filters),
      updated: payload.policyFilter.updated,
    } as ITicketSavedFilter
  }

  const handleDeleteTicketFilter = async (id: string) => {
    const result = await deleteSavedFilter({
      variables: {id: parseId(id)},
    })
    const payload = result.data?.deletePolicyFilter
    if (!payload?.success) {
      handleFilterErrors(payload?.errors, 'Unable to delete filter.')
      return false
    }
    await refetchSavedFilters()
    toast.success('Filter deleted.')
    return true
  }

  const handleApplyTicketFilters = (nextFilters: ITicketFilters) => {
    setFiltersFromObject({...INITIAL_TICKET_FILTERS, ...nextFilters})
    setCurrentPage(1)
  }

  const clearFilters = () => {
    setFiltersFromObject({...INITIAL_TICKET_FILTERS})
    setCurrentPage(1)
  }

  const handleRemoveFilter = (filterKey: string | string[]) => {
    const nextFilters = {...currentFilters}
    const keysToReset = Array.isArray(filterKey) ? filterKey : [filterKey]
    keysToReset.forEach((key) => {
      if (key in INITIAL_TICKET_FILTERS) {
        ;(nextFilters as any)[key] = (INITIAL_TICKET_FILTERS as any)[key]
      }
    })
    setFiltersFromObject(nextFilters)
    setCurrentPage(1)
  }

  const applyQuickSearch = () => {
    const nextValue = quickSearch.trim()
    if (nextValue === search.trim()) {
      refetch?.()
      return
    }
    setSearch(nextValue)
    setCurrentPage(1)
  }

  const usersOptions = users.map((user: any) => ({
    value: user.id,
    label: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
  }))
  const groupsOptions = groups.map((group: any) => ({value: group.id, label: group.name}))
  const ticketTypesOptions = ticketTypes.map((type) => ({value: type.id, label: type.name}))
  const statusOptions = [
    {value: 'OPEN', label: 'Open'},
    {value: 'IN_PROGRESS', label: 'In Progress'},
    {value: 'WAITING_CUSTOMER', label: 'Waiting Customer'},
    {value: 'RESOLVED', label: 'Resolved'},
    {value: 'CLOSED', label: 'Closed'},
  ]
  const channelOptions = [
    {value: 'INTERNAL', label: 'Internal'},
    {value: 'PHONE', label: 'Phone'},
    {value: 'WHATSAPP', label: 'WhatsApp'},
    {value: 'SMS', label: 'SMS'},
    {value: 'EMAIL', label: 'Email'},
    {value: 'WEB', label: 'Web'},
    {value: 'API', label: 'API'},
  ]
  const priorityOptions = [
    {value: 'LOW', label: 'Low'},
    {value: 'MEDIUM', label: 'Medium'},
    {value: 'HIGH', label: 'High'},
    {value: 'URGENT', label: 'Urgent'},
  ]
  const assignmentTypeOptions = [
    {value: 'USER', label: 'User'},
    {value: 'GROUP', label: 'Group'},
  ]
  const inboxModeOptions = [
    {value: 'MY', label: 'My queue'},
    {value: 'GROUP', label: 'Group queue'},
    {value: 'UNASSIGNED', label: 'Unassigned'},
    {value: 'OVERDUE', label: 'Overdue'},
  ]
  const yesNoOptions = [
    {value: true, label: 'Yes'},
    {value: false, label: 'No'},
  ]

  const filtersConfig = [
    {key: 'search', label: 'Search'},
    {key: 'status', label: 'Status', options: statusOptions},
    {key: 'channel', label: 'Channel', options: channelOptions},
    {key: 'priority', label: 'Priority', options: priorityOptions},
    {key: 'ticketTypeId', label: 'Type', options: ticketTypesOptions},
    {key: 'assignmentType', label: 'Assignment Type', options: assignmentTypeOptions},
    {key: 'assignedUserId', label: 'Assigned User', options: usersOptions},
    {key: 'assignedGroupId', label: 'Assigned Group', options: groupsOptions},
    {key: 'createdById', label: 'Creator', options: usersOptions},
    {key: 'inboxMode', label: 'Inbox Mode', options: inboxModeOptions},
    {key: 'pendingInbox', label: 'Pending Inbox', options: yesNoOptions},
    {key: 'onlyOverdue', label: 'Only Overdue', options: yesNoOptions},
    {key: 'onlyUnassigned', label: 'Only Unassigned', options: yesNoOptions},
  ]

  const dateRangeFilters = [
    {keyFrom: 'createdAfter', keyTo: 'createdBefore', label: 'Created Date'},
    {keyFrom: 'dueAfter', keyTo: 'dueBefore', label: 'Due Date'},
  ]

  const onDelete = async (ticket: ITicket) => {
    if (!window.confirm(`Delete ticket #${ticket.id}?`)) return
    const res = await deleteTicket({variables: {id: parseId(ticket.id)}})
    if (res.data?.deleteTicket?.success) {
      toast.success('Ticket deleted')
      refetch?.()
      refetchDashboard?.()
      return
    }
    toast.error(res.data?.deleteTicket?.errors?.[0]?.message || 'Could not delete ticket')
  }

  const onArchive = async (ticket: ITicket, isArchived: boolean) => {
    const actionLabel = isArchived ? 'archive' : 'unarchive'
    if (!window.confirm(`Do you want to ${actionLabel} ticket ${ticket.id}?`)) return
    const res: any = await archiveTicket({
      variables: {
        id: parseId(ticket.id),
        isArchived,
        expectedUpdatedAt: ticket.updatedAt,
      },
    })
    if (res?.data?.archiveTicket?.success) {
      toast.success(`Ticket ${isArchived ? 'archived' : 'unarchived'}`)
      refetch?.()
      refetchDashboard?.()
      return
    }
    toast.error(res?.data?.archiveTicket?.errors?.[0]?.message || 'Could not update archive status')
  }

  const printTicketDocument = (ticketInfo: ITicket) => {
    const directedTo = assignmentLabel(ticketInfo)
    const notes = ticketInfo.notes || []
    const attachments = ticketInfo.attachments || []
    const notesHtml = notes.length
      ? notes
          .slice(0, 20)
          .map(
            (note) => `
              <div class="list-item">
                <div class="list-head">
                  <strong>${escapeHtml(userLabel(note.author))}</strong>
                  <span>${escapeHtml(formatDate(note.createdAt))}</span>
                </div>
                <p>${toPrintText(note.comment)}</p>
              </div>
            `
          )
          .join('')
      : '<p class="muted">No notes registered.</p>'
    const attachmentsHtml = attachments.length
      ? attachments
          .slice(0, 20)
          .map(
            (attachment) => `
              <div class="list-item">
                <div class="list-head">
                  <strong>${escapeHtml(attachment.originalName || attachment.file)}</strong>
                  <span>${escapeHtml(formatDate(attachment.createdAt))}</span>
                </div>
                <p>Uploaded by ${escapeHtml(userLabel(attachment.uploadedBy))}</p>
              </div>
            `
          )
          .join('')
      : '<p class="muted">No attachments uploaded.</p>'
    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Ticket ${escapeHtml(ticketInfo.id)} - Print</title>
          <style>
            @page { size: A4; margin: 14mm; }
            * { box-sizing: border-box; }
            body { font-family: "Segoe UI", Tahoma, Arial, sans-serif; color: #182a4d; margin: 0; }
            .header { border: 1px solid #dbe3f2; background: linear-gradient(135deg, #f7faff, #eff4ff); border-radius: 10px; padding: 14px; }
            .header-top { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; }
            .ticket-id { font-size: 11px; letter-spacing: .04em; text-transform: uppercase; color: #536490; margin-bottom: 4px; }
            h1 { margin: 0; font-size: 23px; line-height: 1.2; }
            .meta { border: 1px solid #dbe3f2; border-radius: 8px; background: #fff; padding: 8px 10px; min-width: 220px; }
            .meta small { display:block; font-size: 10px; text-transform: uppercase; color: #64749a; }
            .meta strong { font-size: 12px; }
            .badges { margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap; }
            .badge { font-size: 10px; font-weight: 700; border: 1px solid transparent; border-radius: 999px; padding: 4px 10px; text-transform: uppercase; }
            .badge-warning { background: #fff6dd; border-color: #fde7ab; color: #9d6600; }
            .badge-primary { background: #e9f1ff; border-color: #cddfff; color: #234f9e; }
            .badge-info { background: #e8f9ff; border-color: #c6effe; color: #0f7398; }
            .badge-success { background: #e7fbef; border-color: #c7f1d7; color: #0f7a43; }
            .badge-secondary { background: #f1f4fb; border-color: #dbe2f1; color: #506089; }
            .badge-danger { background: #ffeaea; border-color: #ffcfcf; color: #b42727; }
            .grid { margin-top: 12px; display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 8px; }
            .item { border: 1px solid #e3e8f3; border-radius: 8px; padding: 8px 10px; background: #fff; min-height: 58px; }
            .item span { display:block; font-size: 10px; color: #6f7d9c; text-transform: uppercase; letter-spacing: .03em; margin-bottom: 2px; }
            .item strong { font-size: 12px; line-height: 1.25; word-break: break-word; }
            .section { margin-top: 10px; border: 1px solid #e3e8f3; border-radius: 8px; background: #f9fbff; padding: 10px; }
            .section h2 { margin: 0 0 6px; font-size: 12px; text-transform: uppercase; letter-spacing: .03em; color: #20305a; }
            .section p { margin: 0; font-size: 12px; line-height: 1.35; color: #2f4069; white-space: pre-wrap; }
            .split { margin-top: 10px; display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 10px; }
            .list-item { border: 1px solid #e2e8f4; border-radius: 8px; background: #fff; padding: 8px; margin-bottom: 8px; }
            .list-head { display: flex; justify-content: space-between; gap: 8px; align-items: center; margin-bottom: 3px; }
            .list-head strong { font-size: 12px; }
            .list-head span { font-size: 10px; color: #6d7b9c; white-space: nowrap; }
            .muted { color: #6d7b9c; }
            .footer { margin-top: 8px; font-size: 10px; color: #60739f; text-align: right; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-top">
              <div>
                <div class="ticket-id">Ticket #${escapeHtml(ticketInfo.id)}</div>
                <h1>${escapeHtml(ticketInfo.title || '-')}</h1>
                <div class="badges">
                  <span class="badge badge-${statusVariant(ticketInfo.status)}">${escapeHtml(enumLabel(ticketInfo.status))}</span>
                  <span class="badge badge-${priorityVariant(ticketInfo.priority)}">${escapeHtml(enumLabel(ticketInfo.priority))}</span>
                  ${ticketInfo.isOverdue ? '<span class="badge badge-danger">Overdue</span>' : ''}
                </div>
              </div>
              <div class="meta">
                <small>Updated</small>
                <strong>${escapeHtml(formatDate(ticketInfo.updatedAt))}</strong>
              </div>
            </div>

            <div class="grid">
              <div class="item"><span>Type</span><strong>${escapeHtml(ticketInfo.ticketType?.name || '-')}</strong></div>
              <div class="item"><span>Channel</span><strong>${escapeHtml(channelLabel(ticketInfo.channel))}</strong></div>
              <div class="item"><span>Directed To</span><strong>${escapeHtml(directedTo)}</strong></div>
              <div class="item"><span>Creator</span><strong>${escapeHtml(userLabel(ticketInfo.createdBy))}</strong></div>
              <div class="item"><span>Last Updated By</span><strong>${escapeHtml(userLabel(ticketInfo.updatedBy))}</strong></div>
              <div class="item"><span>External Reference</span><strong>${escapeHtml(ticketInfo.externalReference || '-')}</strong></div>
              <div class="item"><span>First Response</span><strong>${escapeHtml(formatDate(ticketInfo.firstResponseAt))}</strong></div>
              <div class="item"><span>Due Date</span><strong>${escapeHtml(formatDate(ticketInfo.dueAt))}</strong></div>
              <div class="item"><span>SLA Breached At</span><strong>${escapeHtml(formatDate(ticketInfo.slaBreachedAt))}</strong></div>
            </div>
          </div>

          <div class="section">
            <h2>Observations</h2>
            <p>${toPrintText(ticketInfo.observations?.trim() || 'No observations.')}</p>
          </div>

          <div class="split">
            <div class="section">
              <h2>Notes (${notes.length})</h2>
              ${notesHtml}
            </div>
            <div class="section">
              <h2>Attachments (${attachments.length})</h2>
              ${attachmentsHtml}
            </div>
          </div>

          <div class="footer">Generated ${escapeHtml(new Date().toLocaleString())}</div>
        </body>
      </html>
    `

    const printWindow = window.open('', '_blank', 'width=1024,height=900')
    if (!printWindow) {
      toast.error('Please allow pop-ups to print this preview.')
      return
    }
    printWindow.document.open()
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
    }, 350)
  }

  const onPrintTicketFromTable = async (ticket: ITicket) => {
    setPrintingTicketId(ticket.id)
    try {
      const result = await fetchTicketById({variables: {id: parseId(ticket.id)}})
      const fullTicket = result.data?.ticketById || ticket
      printTicketDocument(fullTicket)
    } catch (err: any) {
      toast.error(`Error preparing print: ${err.message}`)
    } finally {
      setPrintingTicketId(null)
    }
  }

  const SortableHeader = ({
    column,
    label,
    className,
  }: {
    column: TicketSortColumn
    label: string
    className?: string
  }) => {
    const active = sortColumn === column
    return (
      <th
        className={`fw-bold ${className || ''}`.trim()}
        role='button'
        style={{cursor: 'pointer', userSelect: 'none'}}
        onClick={() => {
          if (active) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
            return
          }
          setSortColumn(column)
          setSortDirection('asc')
        }}
      >
        <span className='d-inline-flex align-items-center gap-1'>
          {label}
          <span className='d-inline-flex flex-column' style={{fontSize: '0.58rem', lineHeight: 0.75}}>
            <i
              className='bi bi-caret-up-fill'
              style={{color: active && sortDirection === 'asc' ? '#3e97ff' : '#c4c4c4'}}
            />
            <i
              className='bi bi-caret-down-fill'
              style={{color: active && sortDirection === 'desc' ? '#3e97ff' : '#c4c4c4'}}
            />
          </span>
        </span>
      </th>
    )
  }

  const printPreviewTicket = () => {
    if (!previewTicketInfo) return
    printTicketDocument(previewTicketInfo)
  }

  const getClosedResolvedDateLabel = (ticket: ITicket) => {
    if (!['RESOLVED', 'CLOSED'].includes(ticket.status)) return '-'
    return formatDate(ticket.closedAt)
  }
  const tableColumnCount = showSolvedClosedInList ? 11 : 10

  return (
    <div className='attention-tickets-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3'>Tickets</Breadcrumb.Item>
        </Breadcrumb>
        <Button size='sm' onClick={() => navigate('/tickets/new')}>
          <i className='bi bi-plus fs-5 me-1' />
          New Ticket
        </Button>
      </header>

      <Row className='g-3 mb-4 attention-tickets-dashboard__kpis'>
        <Col xl={2} lg={2} md={4} sm={6} className='attention-tickets-page__kpi-col'>
          <Card className='attention-tickets-page__kpi h-100'>
            <Card.Body>
              <div className='attention-tickets-page__kpi-top'>
                <span className='attention-tickets-page__kpi-icon attention-tickets-page__kpi-icon--blue'>
                  <i className='bi bi-inboxes' />
                </span>
                <span className='attention-tickets-page__kpi-title'>Active Tickets</span>
              </div>
              <div className='attention-tickets-page__kpi-main'>
                <div className='attention-tickets-page__kpi-value'>
                  {dashboardLoading ? <Spinner animation='border' size='sm' /> : activeTicketsCount}
                </div>
                <div className='attention-tickets-page__kpi-meta'>Open Queue</div>
              </div>
              <div className='attention-tickets-page__kpi-foot'>Excludes resolved and closed tickets</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={2} lg={2} md={4} sm={6} className='attention-tickets-page__kpi-col'>
          <Card className='attention-tickets-page__kpi h-100'>
            <Card.Body>
              <div className='attention-tickets-page__kpi-top'>
                <span className='attention-tickets-page__kpi-icon attention-tickets-page__kpi-icon--purple'>
                  <i className='bi bi-exclamation-triangle' />
                </span>
                <span className='attention-tickets-page__kpi-title'>Urgent Tickets</span>
              </div>
              <div className='attention-tickets-page__kpi-main'>
                <div className='attention-tickets-page__kpi-value'>
                  {loadingUrgentTickets ? <Spinner animation='border' size='sm' /> : urgentTicketsCount}
                </div>
                <div className='attention-tickets-page__kpi-meta'>Priority: Urgent</div>
              </div>
              <div className='attention-tickets-page__kpi-foot'>Immediate attention needed</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={2} lg={2} md={4} sm={6} className='attention-tickets-page__kpi-col'>
          <Card className='attention-tickets-page__kpi h-100'>
            <Card.Body>
              <div className='attention-tickets-page__kpi-top'>
                <span className='attention-tickets-page__kpi-icon attention-tickets-page__kpi-icon--gold'>
                  <i className='bi bi-clock-history' />
                </span>
                <span className='attention-tickets-page__kpi-title'>Overdue Tickets</span>
              </div>
              <div className='attention-tickets-page__kpi-main'>
                <div className='attention-tickets-page__kpi-value'>
                  {loadingTicketMetrics ? <Spinner animation='border' size='sm' /> : overdueTicketsCount}
                </div>
                <div className='attention-tickets-page__kpi-meta'>SLA Breach</div>
              </div>
              <div className='attention-tickets-page__kpi-foot'>Past due date</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={2} lg={2} md={4} sm={6} className='attention-tickets-page__kpi-col'>
          <Card className='attention-tickets-page__kpi h-100'>
            <Card.Body>
              <div className='attention-tickets-page__kpi-top'>
                <span className='attention-tickets-page__kpi-icon attention-tickets-page__kpi-icon--blue'>
                  <i className='bi bi-calendar-check' />
                </span>
                <span className='attention-tickets-page__kpi-title'>Tickets Today</span>
              </div>
              <div className='attention-tickets-page__kpi-main'>
                <div className='attention-tickets-page__kpi-value'>{dashboard?.todaySubmitted ?? 0}</div>
                <div className='attention-tickets-page__kpi-meta'>Today</div>
              </div>
              <div className='attention-tickets-page__kpi-foot'>{activeTicketsCount} currently active</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={2} lg={2} md={4} sm={6} className='attention-tickets-page__kpi-col'>
          <Card className='attention-tickets-page__kpi h-100'>
            <Card.Body>
              <div className='attention-tickets-page__kpi-top'>
                <span className='attention-tickets-page__kpi-icon attention-tickets-page__kpi-icon--green'>
                  <i className='bi bi-check2-all' />
                </span>
                <span className='attention-tickets-page__kpi-title'>Resolved Today</span>
              </div>
              <div className='attention-tickets-page__kpi-main'>
                <div className='attention-tickets-page__kpi-value'>{dashboard?.todayResolved ?? 0}</div>
                <div className='attention-tickets-page__kpi-meta'>Today</div>
              </div>
              <div className='attention-tickets-page__kpi-foot'>{resolvedCount} total solved/closed</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className='attention-tickets-page__listing'>
        <div className='attention-tickets-page__table-toolbar'>
          <div className='attention-tickets-page__table-actions'>
            <InputGroup className='attention-tickets-page__quick-search'>
              <input
                type='text'
                className='form-control form-control-sm'
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return
                  e.preventDefault()
                  applyQuickSearch()
                }}
                placeholder='Quick search: ID, subject, reference...'
              />
              <Button
                size='sm'
                variant='outline-success'
                style={{border: '1px solid #dbdfe9'}}
                onClick={applyQuickSearch}
              >
                Search
              </Button>
              <Button
                size='sm'
                variant='outline-primary'
                style={{border: '1px solid #dbdfe9'}}
                onClick={() => {
                  setDraftFilters(currentFilters)
                  setIsOpenFiltersModal(true)
                }}
              >
                Filter
              </Button>
            </InputGroup>
            <Form.Check
              type='switch'
              id='toggle-show-solved-closed'
              className='ms-3'
              label='Show Resolved/Closed'
              checked={showSolvedClosedInList}
              onChange={(e) => {
                setShowSolvedClosedInList(e.target.checked)
                setCurrentPage(1)
              }}
            />
          </div>
          <div className='attention-tickets-page__table-filters-line'>
            <AppliedFiltersList
              filters={appliedFilters as unknown as Record<string, any>}
              config={filtersConfig}
              dateRangeFilters={dateRangeFilters}
              formatDate={formatDate}
              clearFilters={clearFilters}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>
          <PaginationSection
            first={first}
            setFirst={(size) => {
              setFirst(size)
              setCurrentPage(1)
            }}
            currentPage={currentPage}
            totalPages={totalPages}
            start={start}
            end={end}
            totalPolicies={totalTickets}
            updatePage={setCurrentPage}
            PaginationUtils={PaginationUtils}
          />
        </div>
        <div className='table-responsive table-scrollable table-container mt-3'>
          <Table hover className='table-scroll attention-tickets-page__table'>
            <thead>
              <tr>
                <SortableHeader column='id' label='ID' />
                <SortableHeader column='subject' label='Subject' />
                <th className='fw-bold'>Status</th>
                <th className='fw-bold'>Type</th>
                <th className='fw-bold'>Channel</th>
                <th className='fw-bold'>Directed To</th>
                <th className='fw-bold'>Creator</th>
                <th className='fw-bold'>Due</th>
                <SortableHeader column='createdAt' label='Created' />
                {showSolvedClosedInList && <th className='fw-bold'>Closed / Resolved</th>}
                <th className='text-end fw-bold'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={tableColumnCount}><div className='d-flex flex-center py-10'><Spinner animation='border' /></div></td>
                </tr>
              )}
              {!loading && tickets.length === 0 && (
                <tr>
                  <td colSpan={tableColumnCount}><div className='d-flex flex-center py-10 text-muted'>No tickets found.</div></td>
                </tr>
              )}
              {!loading && sortedTickets.map((ticket) => (
                <tr key={ticket.id} className={ticket.isOverdue ? 'table-warning' : ticket.isArchived ? 'table-secondary' : ''}>
                  <td>{ticket.id}</td>
                  <td>
                    <button
                      type='button'
                      className='attention-tickets-page__subject-btn text-primary text-link fw-bold'
                      onClick={() => navigate(`/tickets/${parseId(ticket.id)}`)}
                    >
                      {ticket.title || '-'}
                    </button>
                  </td>
                  <td>
                    <div className='attention-tickets-page__status-priority'>
                      <span className={`attention-tickets-page__pill ${tableStatusBadgeClass(ticket.status)}`}>
                        {enumLabel(ticket.status)}
                      </span>
                      <span className={`attention-tickets-page__pill ${tablePriorityBadgeClass(ticket.priority)}`}>
                        {ticket.priority ? enumLabel(ticket.priority) : 'No Priority'}
                      </span>
                    </div>
                  </td>
                  <td>{ticket.ticketType?.name || '-'}</td>
                  <td>{channelLabel(ticket.channel)}</td>
                  <td>{assignmentLabel(ticket)}</td>
                  <td>{userLabel(ticket.createdBy)}</td>
                  <td>{ticket.dueAt ? formatDate(ticket.dueAt) : '-'}</td>
                  <td>{formatDate(ticket.createdAt)}</td>
                  {showSolvedClosedInList && <td>{getClosedResolvedDateLabel(ticket)}</td>}
                  <td className='text-end'>
                    <Dropdown
                      as={ButtonGroup}
                      align='end'
                      className='ticket-actions-dropdown'
                      popperConfig={{strategy: 'fixed'}}
                    >
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        className='ticket-actions__preview-btn'
                        onClick={() => setPreviewTicket(ticket)}
                      >
                        Preview
                      </Button>
                      <Dropdown.Toggle
                        split
                        variant='outline-secondary'
                        size='sm'
                        className='ticket-actions__toggle-btn'
                        id={`ticket-actions-${ticket.id}`}
                      />
                      <Dropdown.Menu className='ticket-actions__menu'>
                        <Dropdown.Item
                          onClick={() => onPrintTicketFromTable(ticket)}
                          disabled={printingTicketId === ticket.id}
                        >
                          {printingTicketId === ticket.id ? 'Preparing print...' : 'Print'}
                        </Dropdown.Item>
                        {canArchiveTicket && (
                          <Dropdown.Item onClick={() => onArchive(ticket, !ticket.isArchived)}>
                            {ticket.isArchived ? 'Unarchive' : 'Archive'}
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item onClick={() => navigate(`/tickets/${parseId(ticket.id)}`)}>Edit</Dropdown.Item>
                        {canDeleteTicket && (
                          <>
                            <Dropdown.Divider />
                            <Dropdown.Item className='text-danger' onClick={() => onDelete(ticket)}>
                              Delete
                            </Dropdown.Item>
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <ModalTicketFilters
        isOpen={isOpenFiltersModal}
        onClose={() => setIsOpenFiltersModal(false)}
        filters={draftFilters}
        setFilters={setDraftFilters}
        onApply={(nextFilters) => {
          handleApplyTicketFilters(nextFilters)
          setIsOpenFiltersModal(false)
        }}
        savedFilters={savedTicketFilters}
        onSaveFilter={handleSaveTicketFilter}
        onUpdateFilter={handleUpdateTicketFilter}
        onDeleteFilter={handleDeleteTicketFilter}
        users={users}
        groups={groups}
        ticketTypes={ticketTypes}
      />

      <Modal show={Boolean(previewTicket)} onHide={() => setPreviewTicket(null)} size='xl' centered>
        <Modal.Header closeButton>
          <Modal.Title>Ticket Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewTicketInfo &&
            (() => {
              const directedTo =
                assignmentLabel(previewTicketInfo)
              const notes = previewTicketInfo.notes || []
              const attachments = previewTicketInfo.attachments || []
              const showingPartialData =
                previewTicketLoading ||
                Boolean(previewTicketError) ||
                previewTicketData?.ticketById?.id !== previewTicketInfo.id
              return (
                <div className='ticket-preview ticket-preview--printlike'>
                  <div className='ticket-preview__hero'>
                    <div className='ticket-preview__hero-main'>
                      <div className='ticket-preview__ticket-id'>Ticket #{previewTicketInfo.id}</div>
                      <h5 className='ticket-preview__subject'>{previewTicketInfo.title || '-'}</h5>
                      <div className='ticket-preview__badges'>
                        <span className={`ticket-preview__badge ticket-preview__badge--${statusVariant(previewTicketInfo.status)}`}>
                          {enumLabel(previewTicketInfo.status)}
                        </span>
                        <span className={`ticket-preview__badge ticket-preview__badge--${priorityVariant(previewTicketInfo.priority)}`}>
                          {enumLabel(previewTicketInfo.priority)}
                        </span>
                        {previewTicketInfo.isOverdue && (
                          <span className='ticket-preview__badge ticket-preview__badge--danger'>Overdue</span>
                        )}
                      </div>
                      {showingPartialData && (
                        <div className='ticket-preview__loading-note'>
                          Loading complete preview details...
                        </div>
                      )}
                    </div>
                    <div className='ticket-preview__hero-side'>
                      <small>Updated</small>
                      <strong>{formatDate(previewTicketInfo.updatedAt)}</strong>
                    </div>
                  </div>

                  <div className='ticket-preview__grid'>
                    <div className='ticket-preview__item'>
                      <span>Type</span>
                      <strong>{previewTicketInfo.ticketType?.name || '-'}</strong>
                    </div>
                    <div className='ticket-preview__item'>
                      <span>Channel</span>
                      <strong>{channelLabel(previewTicketInfo.channel)}</strong>
                    </div>
                    <div className='ticket-preview__item'>
                      <span>Directed To</span>
                      <strong>{directedTo}</strong>
                    </div>
                    <div className='ticket-preview__item'>
                      <span>Creator</span>
                      <strong>{userLabel(previewTicketInfo.createdBy)}</strong>
                    </div>
                    <div className='ticket-preview__item'>
                      <span>Last Updated By</span>
                      <strong>{userLabel(previewTicketInfo.updatedBy)}</strong>
                    </div>
                    <div className='ticket-preview__item'>
                      <span>External Reference</span>
                      <strong>{previewTicketInfo.externalReference || '-'}</strong>
                    </div>
                    <div className='ticket-preview__item'>
                      <span>First Response</span>
                      <strong>{formatDate(previewTicketInfo.firstResponseAt)}</strong>
                    </div>
                    <div className='ticket-preview__item'>
                      <span>Due Date</span>
                      <strong>{formatDate(previewTicketInfo.dueAt)}</strong>
                    </div>
                    <div className='ticket-preview__item'>
                      <span>SLA Breached At</span>
                      <strong>{formatDate(previewTicketInfo.slaBreachedAt)}</strong>
                    </div>
                  </div>

                  <div className='ticket-preview__section'>
                    <h6>Observations</h6>
                    <p>{previewTicketInfo.observations?.trim() || 'No observations.'}</p>
                  </div>

                  <div className='ticket-preview__split'>
                    <div className='ticket-preview__section'>
                      <h6>Notes ({notes.length})</h6>
                      {notes.length === 0 && <p>No notes registered.</p>}
                      {notes.length > 0 && (
                        <ul className='ticket-preview__list'>
                          {notes.slice(0, 3).map((note) => (
                            <li key={note.id} className='ticket-preview__list-item'>
                              <div className='ticket-preview__list-title'>
                                <strong>{userLabel(note.author)}</strong>
                                <span>{formatDate(note.createdAt)}</span>
                              </div>
                              <p>{note.comment || '-'}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className='ticket-preview__section'>
                      <h6>Attachments ({attachments.length})</h6>
                      {attachments.length === 0 && <p>No attachments uploaded.</p>}
                      {attachments.length > 0 && (
                        <ul className='ticket-preview__list'>
                          {attachments.slice(0, 3).map((attachment) => (
                            <li key={attachment.id} className='ticket-preview__list-item'>
                              <div className='ticket-preview__list-title'>
                                <strong>{attachment.originalName || attachment.file}</strong>
                                <span>{formatDate(attachment.createdAt)}</span>
                              </div>
                              <p>Uploaded by {userLabel(attachment.uploadedBy)}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className='ticket-preview__generated'>Generated {new Date().toLocaleString()}</div>
                </div>
              )
            })()}
        </Modal.Body>
        <Modal.Footer>
          {previewTicketInfo && (
            <Button variant='outline-dark' size='sm' onClick={printPreviewTicket}>
              <i className='bi bi-printer me-1' />
              Print
            </Button>
          )}
          <Button variant='secondary' size='sm' onClick={() => setPreviewTicket(null)}>Close</Button>
          {previewTicketInfo && (
            <Button size='sm' onClick={() => navigate(`/tickets/${parseId(previewTicketInfo.id)}`)}>Open Ticket</Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  )
}
